import { LocalStorageService } from '../localStorage/localStorage.service';
import { localStorageKeys } from '../localStorage/localStorageVariables';

// Attribution lives in sessionStorage so it retires when the browsing session (tab) ends,
// with an absolute 24h cap as a backstop for very long-lived tabs. Only the durable tierId
// is kept in localStorage (membership intent) — the location/object attribution is never
// persisted long-term.
const AFFILIATE_SESSION_KEY = 'affiliate_session';
const AFFILIATE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const TRAVELVERSE_SSO_PARAMS = [
  'objectName',
  'objectType',
  'language',
  'currency',
  'country',
  'city',
  'address',
  'latitude',
  'longitude',
  'tierId',
  'tierid',
];

function isTravelverseLandingUrl(url: string): boolean {
  const parsed = new URL(url, window.location.origin);
  return (
    parsed.searchParams.has('objectName') ||
    parsed.searchParams.has('objectType')
  );
}

function generateClickId(): string {
  // Mirrors the backend clickoutId shape ("<scope>_<unique>"). Each affiliate
  // landing gets a fresh id so attribution maps 1:1 to a partner click.
  return (
    'tvx_' +
    Date.now().toString(36) +
    '_' +
    Math.random().toString(36).slice(2, 10)
  );
}

/**
 * Captures partner/affiliate landing parameters and mints a fresh clickId for the landing.
 * The full attribution blob is written to the sessionStorage attribution session (retires on
 * tab close + 24h cap); only the durable tierId is kept in localStorage[REFERAL_PARAMETER].
 * Returns true when a landing was detected & captured.
 */
export function captureTravelverseSsoFromUrl(
  localStorageService: LocalStorageService,
  sourceUrl?: string
): boolean {
  const url = sourceUrl || window.location.href;
  if (!isTravelverseLandingUrl(url)) {
    return false;
  }

  const parsed = new URL(url, window.location.origin);
  const existing = JSON.parse(
    localStorageService.getItem(localStorageKeys.REFERAL_PARAMETER) || '{}'
  );
  const ssoData: Record<string, string> = { ...existing };

  TRAVELVERSE_SSO_PARAMS.forEach((key) => {
    const value = parsed.searchParams.get(key);
    if (value) {
      ssoData[key === 'tierid' ? 'tierId' : key] = value;
    }
  });

  // A fresh landing = a fresh attribution click.
  ssoData.clickId = generateClickId();
  ssoData.landedAt = new Date().toISOString();

  // Durable store: ONLY tierId persists in localStorage (membership intent). Location/object
  // attribution is intentionally not persisted here so it can't outlive the session.
  const tierId = ssoData.tierId || existing.tierId;
  localStorageService.setItem(
    localStorageKeys.REFERAL_PARAMETER,
    JSON.stringify(tierId ? { tierId } : {})
  );

  // Session store: the full attribution blob, retired when the tab/session ends + 24h cap.
  try {
    sessionStorage.setItem(AFFILIATE_SESSION_KEY, JSON.stringify(ssoData));
  } catch {
    /* sessionStorage unavailable (private mode quota) — attribution simply won't ride */
  }

  if (!sourceUrl) {
    clearTravelverseSsoParamsFromUrl();
  }
  return true;
}

/**
 * Reads the active affiliate attribution session from sessionStorage, enforcing the 24h
 * absolute cap. Returns null (and clears the session) when absent or expired. This is the
 * single source of truth for "is there an active affiliate landing this session".
 */
export function readAffiliateSession(): Record<string, any> | null {
  try {
    const raw = sessionStorage.getItem(AFFILIATE_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const landed = data?.landedAt ? Date.parse(data.landedAt) : NaN;
    if (isFinite(landed) && Date.now() - landed > AFFILIATE_MAX_AGE_MS) {
      sessionStorage.removeItem(AFFILIATE_SESSION_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Affiliate dimensions that should ride along with every analytics event for the active
 * session. Reads the sessionStorage attribution session (so they auto-stop once it retires),
 * with no DI dependency on the affiliate service (avoids a circular dependency).
 */
export function getAffiliateAnalyticsDimensions(): Record<string, string> {
  const ctx = readAffiliateSession();
  if (!ctx || (!ctx.objectName && !ctx.objectType && !ctx.clickId)) {
    return {};
  }
  const dims: Record<string, string> = {};
  if (ctx.clickId) dims.affiliate_click_id = ctx.clickId;
  if (ctx.objectName) dims.affiliate_object_name = ctx.objectName;
  if (ctx.objectType) dims.affiliate_object_type = ctx.objectType;
  return dims;
}

export function clearTravelverseSsoParamsFromUrl(): void {
  if (!window.history.replaceState) {
    return;
  }

  const url = new URL(window.location.href);
  let changed = false;

  TRAVELVERSE_SSO_PARAMS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (changed) {
    const query = url.searchParams.toString();
    window.history.replaceState(
      {},
      document.title,
      url.pathname + (query ? '?' + query : '')
    );
  }
}
